import { AnimationEvent } from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { BaseComponent } from '@core/components/base-component';
import { EstimateService } from '@shared/services/estimate.service';
import { ProjectService } from '@shared/services/project.service';
import { UserService } from '@shared/services/user.service';
import { lastValueFrom, of, switchMap } from 'rxjs';
import { EstimateImportService } from '../project-import/shared/estimate-import.service';
import { ProjectPickerItem } from './models/project-picker.item';

@Component({
  selector: 'pop-project-picker',
  templateUrl: './project-picker.component.html',
  styleUrls: ['./project-picker.component.less']
})
export class ProjectPickerComponent extends BaseComponent implements OnInit {
  public filterMyProjects = false;

  @Input()
  public readonly = false;
  @Input()
  public importMode = false;
  @Input()
  public styleClass: 'rem-fix' | '' = '';

  @Input() public style?:{};

  private _selectedProjectCode: string | undefined;
  @Input()
  public get selectedProjectCode() {
    return this._selectedProjectCode;
  }
  public set selectedProjectCode(value: string | undefined) {
    if (this._selectedProjectCode !== value) {
      this._selectedProjectCode = value;
      this.selectedProjectCodeChange.emit(value);

      this.updateSelected();
    }
  }

  @Output()
  public selectedProjectCodeChange = new EventEmitter<string | undefined>();

  public projects: ProjectPickerItem[];

  private _selectedProject: ProjectPickerItem;
  @Input()
  public get selectedProject() {
    return this._selectedProject;
  }
  public set selectedProject(value: ProjectPickerItem) {
    value = value ?? undefined; // PrimeNG returns null this code avoids ExpressionChangedAfterItHasBeenCheckedError
    if (this._selectedProject !== value) {
      this._selectedProject = value;
      this.selectedProjectChange.emit(value);
      this.selectedProjectCode = value?.project?.code;
    }
  }

  @Output()
  public selectedProjectChange = new EventEmitter<ProjectPickerItem>();

  private allProjects: ProjectPickerItem[];

  private _projectFilter: (project: ProjectPickerItem) => boolean;
  @Input()
  public get projectFilter() {
    return this._projectFilter;
  }
  public set projectFilter(value: (project: ProjectPickerItem) => boolean) {
    this._projectFilter = value;
    this.filter();
  }

  private projectCodes?: string[];

  public constructor(
    private estimateImportService: EstimateImportService,
    private estimateService: EstimateService,
    private projectService: ProjectService,
    private userService: UserService,
    private renderer: Renderer2
  ) {
    super();
  }

  public async ngOnInit() {
    this.subscribe(
      this.userService
        .getUser()
        .pipe(
          switchMap(user =>
            user.resourceCode ? this.projectService.getProjectCodesByUserCode(user.resourceCode) : of([] as string[])
          )
        )
        .subscribe(codes => (this.projectCodes = codes))
    );

    if (!this.importMode) {
      const projects = await lastValueFrom(this.estimateService.getProjects());
      this.allProjects = projects.map(p => {
        const item: ProjectPickerItem = {
          project: p,
          code: p.code
        };
        return item;
      });

      this.filter();
      this.updateSelected();
    } else {
      this.subscribe(
        this.estimateImportService.getProjectCodesNotInEstimate().subscribe(projects => {
          this.allProjects = projects.map(p => {
            const item: ProjectPickerItem = {
              ...p,
              code: p.project.code
            };
            return item;
          });
          this.filter();
          this.updateSelected();
        })
      );
    }
  }

  public appendFilterButton(matBtn: MatButton, event: AnimationEvent) {
    const element = event.element as HTMLElement;

    const header = element.querySelector('.p-dropdown-header');
    const exisitingBtn = element.querySelector('.myprojects-button');

    const buttonElement = matBtn._elementRef as ElementRef<HTMLButtonElement>;
    const newBtn = buttonElement.nativeElement;
    if (exisitingBtn) return;

    this.renderer.setStyle(newBtn, 'display', 'inline-flex');
    this.renderer.insertBefore(header, newBtn, header.children[0]);
  }

  public toggleMyProjectsFilter(e: Event) {
    e.stopPropagation();
    this.filterMyProjects = !this.filterMyProjects;
    this.filter();
  }

  private updateSelected() {
    if (!this.allProjects) return;

    this.selectedProject = this.allProjects?.find(p => p.project.code === this.selectedProjectCode);
  }

  public filter() {
    this.projects = this.allProjects ?? [];

    if (this.projectFilter) {
      this.projects = this.projects.filter(dto => this.projectFilter(dto));
    }

    if (this.filterMyProjects) {
      this.projects = this.projects.filter(p => this.projectCodes.includes(p.code));
    }
  }
}
